import Routing from "./Routing";
import Providers from "./Providers";
import React, { Suspense } from "react";
import { getSettings } from "./common/settingsProvider";
import PrivacyAuthProvider from "./auth/PrivacyAuthProvider";
import ExternalUserLoader from "./auth/ExternalUserLoader";
import UserContextProvider from "./auth/userContextProvider/UserContextProvider";
import CustomerRedirectWrapper from "./auth/customerRedirect/CustomerRedirectWrapper";
import { TranslationProvider } from "./localization/LocalizationContext";
import ErrorBoundaryWrapper from "./errorPage/ErrorBoundaryWrapper";
import ErrorRouterSwitch from "./ErrorRouterSwitch";
import "./pdf.css";
import UserRouterSwitch from "./auth/UserRouterSwitch";
import { setupErrorHandling } from "./common/errorHandling";
import PlanProvider from "./auth/planProvider/PlanProvider";
import { DotLegalBreadCrumbModel, DotLegalFullScreenSpinner } from "@dotlegal/dotlegal-ui-components";
import { Route, Routes } from "react-router";
import MicroFrontend from "./microFrontend/MicroFrontend";
import RedirectLegacyExternalQuestionnaireUrls from "./RedirectLegacyExternalQuestionnaireUrls.tsx";
import PublicInternalAssessmentInitiation from "./internalAssessment/publicInternalAssessmentTemplate/publicInitiation/PublicInternalAssessmentInitiation.tsx";
import PublicInternalAssessmentReceipt from "./internalAssessment/publicInternalAssessmentTemplate/publicInitiation/PublicInternalAssessmentReceipt.tsx";
import PublicPolicyAcknowledgement from "./policy/publicPolicyAcknowledgement/PublicPolicyAcknowledgement.tsx";
import PublicRiskObservation from "./risk/publicRiskObservation/PublicRiskObservation.tsx";
import LegalEntityAuditGroupManagementReport from "./legalEntityAuditGroup/legalEntityAuditGroupManagementReport/LegalEntityAuditGroupManagementReport.tsx";
import InternalAssessmentGroupManagementReport from "./internalAssessment/internalAssessmentGroupManagementReport/InternalAssessmentGroupManagementReport.tsx";
import ComplianceReport from "./dashboard/complianceReport/ComplianceReport.tsx";
import RiskManagementReportPdf from "./risk/riskManagementReport/riskManagementReportPdf/RiskManagementReportPdf.tsx";
import RiskGovernanceOverviewPdf from "./risk/riskGovernanceOverview/riskGovernanceOverviewPdf/RiskGovernanceOverviewPdf.tsx";
import HubSpotTrackingProvider from "./auth/HubSpotTrackingProvider.tsx";
import IncidentLogPdf from "./incidentLog/incidentLogPdf/IncidentLogPdf.tsx";
import OverdueTasksProvider from "./common/providers/overdueTasksProvider/OverdueTasksProvider.tsx";
import ObservationsProvider from "@common/providers/observationsProvider/ObservationsProvider";
import RiskAreasProvider from "./common/providers/riskAreasProvider/RiskAreasProvider.tsx";
import LegalEntityAuditClassificationReport from "./legalEntity/legalEntityAuditClassificationReport/LegalEntityAuditClassificationReport.tsx";
import TaskPdf from "./taskManagement/taskPdf/TaskPdf.tsx";
import PolicyPdf from "./policy/policyPdf/PolicyPdf.tsx";
import DocumentPreviewPage from "./common/components/documentPreview/DocumentPreviewPage.tsx";
import Main from "./main/Main.tsx";
import RouterSwitch from "./RouterSwitch.tsx";
import DeveloperErrorOverlay from "./errorPage/DeveloperErrorOverlay.tsx";
import CustomerDialog from "./auth/customerDialog/CustomerDialog.tsx";

setupErrorHandling();

// Federation runtime only works in production builds — skip in dev to avoid CommonJS interop errors.
// Date suffix busts the cache: remoteEntry.js's internal JS references change on each microfrontend release, so caching leads to 404s.
if (import.meta.env.PROD) {
    import("@module-federation/enhanced/runtime").then(({ init }) => {
        init({
            name: "privacyHost",
            remotes: [
                {
                    name: "questionnaireService",
                    entry: getSettings().questionnaireFrontEndUrl + "/remoteEntry.js?" + new Date().getTime(),
                    type: "module",
                },
            ],
        });
    });
}

export interface IAppProps {
    breadCrumbsCallback?: (breadCrumbs: Array<DotLegalBreadCrumbModel>) => void;
}

function App(props: IAppProps) {
    let content = (
        <TranslationProvider>
            <ErrorRouterSwitch>
                <ErrorBoundaryWrapper>
                    <Routes>
                        <Route path="privacy/start-internal-assessment">
                            <Route path=":externalId" element={<PublicInternalAssessmentInitiation />} />
                            <Route path=":externalId/receipt" element={<PublicInternalAssessmentReceipt />} />
                        </Route>
                        <Route path="privacy/policy-acknowledgement/:acknowledgementId" element={<PublicPolicyAcknowledgement />} />
                        <Route path="privacy/public/report-observation/:externalId" element={<PublicRiskObservation />} />
                        <Route
                            path={"*"}
                            element={
                                <PrivacyAuthProvider>
                                    <UserRouterSwitch>
                                        <ExternalUserLoader>
                                            <CustomerRedirectWrapper>
                                                <CustomerDialog>
                                                    <UserContextProvider>
                                                        <OverdueTasksProvider>
                                                            <PlanProvider>
                                                                <RiskAreasProvider>
                                                                    <HubSpotTrackingProvider>
                                                                        <Routes>
                                                                            {/* "no-menu" is for reports shown from Privacy that requires login*/}
                                                                            <Route path="privacy/questionnaires/no-menu/*" element={<MicroFrontend />} />
                                                                            <Route
                                                                                path="privacy/audit-group/:id/management-report"
                                                                                element={<LegalEntityAuditGroupManagementReport />}
                                                                            />
                                                                            <Route
                                                                                path="privacy/assessment-group/:id/management-report"
                                                                                element={<InternalAssessmentGroupManagementReport />}
                                                                            />
                                                                            <Route path="privacy/incident-log/:id/pdf" element={<IncidentLogPdf />} />
                                                                            <Route
                                                                                path="privacy/dashboard/compliance-report/:id"
                                                                                element={<ComplianceReport />}
                                                                            />
                                                                            <Route
                                                                                path="privacy/risk/management-report/:id"
                                                                                element={<RiskManagementReportPdf />}
                                                                            />
                                                                            <Route
                                                                                path="privacy/risk/governance-overview-pdf/:riskAreaId"
                                                                                element={<RiskGovernanceOverviewPdf />}
                                                                            />
                                                                            <Route
                                                                                path="privacy/riskClassificationReport/:id/:scope"
                                                                                element={<LegalEntityAuditClassificationReport type="classification" />}
                                                                            />
                                                                            <Route
                                                                                path="privacy/riskAuditReport/:id/:scope"
                                                                                element={<LegalEntityAuditClassificationReport type="audit" />}
                                                                            />
                                                                            <Route path="privacy/task-management/:taskId/pdf" element={<TaskPdf />} />
                                                                            <Route
                                                                                path="privacy/policy/:policyId/version/:versionId/pdf"
                                                                                element={<PolicyPdf />}
                                                                            />
                                                                            <Route
                                                                                path="privacy/document-preview/:entityType/:entityId/:documentId"
                                                                                element={<DocumentPreviewPage />}
                                                                            />
                                                                            <Route
                                                                                path="*"
                                                                                element={
                                                                                    // Only pages with the top bar need it; the report and PDF routes above would just poll
                                                                                    <ObservationsProvider>
                                                                                        <Main>
                                                                                            <RouterSwitch />
                                                                                        </Main>
                                                                                    </ObservationsProvider>
                                                                                }
                                                                            />
                                                                        </Routes>
                                                                    </HubSpotTrackingProvider>
                                                                </RiskAreasProvider>
                                                            </PlanProvider>
                                                        </OverdueTasksProvider>
                                                    </UserContextProvider>
                                                </CustomerDialog>
                                            </CustomerRedirectWrapper>
                                        </ExternalUserLoader>
                                    </UserRouterSwitch>
                                </PrivacyAuthProvider>
                            }
                        />
                    </Routes>
                </ErrorBoundaryWrapper>
            </ErrorRouterSwitch>
        </TranslationProvider>
    );

    return (
        <React.StrictMode>
            <Suspense fallback={<DotLegalFullScreenSpinner />}>
                <Routing>
                    <Providers breadCrumbsCallback={props.breadCrumbsCallback}>
                        <Routes>
                            {/* "external" is for all external responses that are anonymous and does not require a menu*/}
                            <Route path="privacy/questionnaires/external/*" element={<MicroFrontend />} />
                            <Route path="privacy/questionnaires/response/*" element={<RedirectLegacyExternalQuestionnaireUrls />} />
                            <Route path="*" element={content} />
                        </Routes>
                    </Providers>
                </Routing>
                {import.meta.env.DEV && <DeveloperErrorOverlay />}
            </Suspense>
        </React.StrictMode>
    );
}

export default App;
